import { useSyncExternalStore } from 'react';

/**
 * 管理アプリの「新バージョン検出 → 任意更新」ストア + ポーリング (2026-06-12)。
 *
 * 管理アプリは PWA / Service Worker を持たない (常時オンラインの管理ツールで、stale な管理 UI を
 * キャッシュするのはむしろ有害) ため、ユーザーアプリの SW ベース更新検知 (features/shell/swUpdate)
 * は使えない。代わりに **ビルド時に焼き込んだ build id (`__BUILD_ID__`)** と、サーバ上の
 * `version.json` を定期照合し、差があれば「新しいバージョンがあります」を UpdateBanner で通知する。
 * 更新は `window.location.reload()` で行う (SW が無いのでリロードすれば最新の index.html +
 * ハッシュ付き chunk を取得する)。UX はユーザーアプリの UpdateBanner と同一。
 */

/** ビルド時に vite define で焼き込まれる build id。test 等で未定義のときは空文字。 */
const RUNNING_BUILD_ID: string = typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : '';

type Listener = () => void;
let updateAvailable = false;
const listeners = new Set<Listener>();

function emit(): void {
  for (const l of [...listeners]) {
    try {
      l();
    } catch {
      /* 1 つの listener 失敗を他に波及させない */
    }
  }
}

/** 新版検出時に呼ぶ (バナー表示をトリガ)。二重通知は no-op。 */
export function markUpdateAvailable(): void {
  if (updateAvailable) return;
  updateAvailable = true;
  emit();
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): boolean {
  return updateAvailable;
}

function getServerSnapshot(): boolean {
  return false;
}

/** 新版あり状態を購読する React hook (UpdateBanner が使用)。 */
export function useUpdateAvailable(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/** 「更新」押下: 管理アプリは SW 無しのため通常リロードで最新版を取得する。 */
export function applyUpdate(): void {
  if (typeof window !== 'undefined') window.location.reload();
}

/**
 * fetched build id が稼働中と異なる「新版」か判定 (pure)。
 * 空文字 / 同一 / running 不明は false。
 */
export function isNewerVersion(running: string, fetched: unknown): boolean {
  return (
    typeof fetched === 'string' && fetched.length > 0 && running.length > 0 && fetched !== running
  );
}

let pollingStarted = false;

/**
 * version.json ポーリングを開始する (main.tsx から 1 回呼ぶ)。
 *   - 本番ビルドのみ (dev は version.json 不在 + build id が毎起動変わるため skip)。
 *   - 一定間隔 + タブ可視化 / フォーカス時に照合 (長時間開きっぱなしのタブにも届く)。
 *   - 既に更新検出済 or 二重起動は no-op。
 *   - 取得失敗 (オフライン / 一時障害) は無視し、次回照合でリトライ。
 */
export function startVersionPolling(intervalMs = 5 * 60 * 1000): void {
  if (pollingStarted) return;
  if (typeof window === 'undefined') return;
  if (!import.meta.env.PROD) return; // dev は skip
  pollingStarted = true;

  const check = async (): Promise<void> => {
    if (updateAvailable) return;
    try {
      const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) return;
      const data = (await res.json()) as { buildId?: unknown };
      if (isNewerVersion(RUNNING_BUILD_ID, data.buildId)) markUpdateAvailable();
    } catch {
      /* オフライン / 一時失敗は無視 (次回照合でリトライ) */
    }
  };

  window.setInterval(() => void check(), intervalMs);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void check();
  });
  window.addEventListener('focus', () => void check());
  void check();
}

/** test 用: 内部状態をリセット。 */
export function __resetAppVersion(): void {
  updateAvailable = false;
  listeners.clear();
  pollingStarted = false;
}
